import React, { useState, useEffect, useContext } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { KaizenThemeContext } from '@kaizen-ui/styles'
import { Slider as RSlider, RangeSlider } from 'rsuite'
import Styles from './styles'

export interface BaseSliderProps {
  className?: string
  graduated?: boolean
  min?: number
  max?: number
  progress?: boolean
  renderMark?: (mark: number) => React.ReactNode
  renderTooltip?: (value: number) => string
  step?: number
}

export interface SliderProps extends BaseSliderProps {
  defaultValue?: number
  onChange?: (value: number) => void
  value?: number
}

const SliderComponent = styled(RSlider)`
  ${(props) => Styles.slider(props.theme)}
`
SliderComponent.displayName = 'Slider'

function Slider({
  className,
  defaultValue = 0,
  graduated = false,
  max = 100,
  min = 0,
  onChange,
  progress = false,
  renderMark = (mark: number) => mark,
  renderTooltip = (v: number) => `${v}%`,
  step = 1,
  value,
}: SliderProps) {
  const theme = useContext(KaizenThemeContext)
  const [sliderValue, setSliderValue] = useState(defaultValue)

  useEffect(() => {
    if (value) setSliderValue(value)
  }, [value])
  useEffect(() => {
    setSliderValue(defaultValue)
  }, [defaultValue])

  const onSliderChanged = (val: number) => {
    setSliderValue(val)
    onChange?.(val)
  }

  return (
    <ThemeProvider theme={theme}>
      <SliderComponent
        className={className}
        data-testid="kui-slider"
        graduated={graduated}
        max={max}
        min={min}
        onChange={onSliderChanged}
        progress={progress}
        renderMark={renderMark}
        renderTooltip={renderTooltip}
        step={step}
        value={value ?? sliderValue}
      />
    </ThemeProvider>
  )
}

export interface RangeProps extends BaseSliderProps {
  defaultValues?: number[]
  onChange?: (value: number[]) => void
  values?: number[]
}

const RangeComponent = styled(RangeSlider)`
  ${(props) => Styles.slider(props.theme)}
`
RangeComponent.displayName = 'Range'

function Range({
  className,
  defaultValues = [0, 100],
  graduated = false,
  max = 100,
  min = 0,
  progress = false,
  onChange,
  renderMark = (mark: number) => mark,
  renderTooltip = (v: number) => `${v}%`,
  step = 1,
  values,
}: RangeProps) {
  const theme = useContext(KaizenThemeContext)
  const [sliderValue, setSliderValue] = useState(defaultValues)

  useEffect(() => {
    if (values) setSliderValue(values)
  }, [values])
  useEffect(() => {
    setSliderValue(defaultValues)
  }, [defaultValues])

  const onSliderChanged = (val: number[]) => {
    setSliderValue(val)
    onChange?.(val)
  }

  return (
    <ThemeProvider theme={theme}>
      <RangeComponent
        className={className}
        data-testid="kui-range"
        graduated={graduated}
        max={max}
        min={min}
        onChange={onSliderChanged}
        progress={progress}
        renderMark={renderMark}
        renderTooltip={renderTooltip}
        step={step}
        value={values ?? sliderValue}
      />
    </ThemeProvider>
  )
}
export { Range, Styles }
export default Slider
